import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices/DomainServicesContext";
import {
  type CreateSessionRequest,
  type SessionFileContribution,
  type SessionRecord,
} from "@/shared/domain/sessions";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import { sessionQueryKeys } from "./sessionQueryKeys";

export interface UseCreateSessionFromFilesVariables {
  request: CreateSessionRequest;
  /**
   * Whole-file contributions to attach after the session is created. Empty
   * is allowed — the session is created with no contributions and no second
   * call is made.
   */
  files: SessionFileContribution[];
  /**
   * The caller's current org id — always required. Sent as the caller-org
   * header on the create call and as the resource-owner header on the attach
   * and rollback-delete calls. Those are distinct backend header semantics
   * (`REQUIRES_CALLER_ORG` vs `REQUIRES_OWNER_ORG`); one field can serve both
   * only because both headers carry the caller's current org id, so do not
   * pass an owner org that differs from the caller's org.
   */
  resourceOwnerId: string;
}

/**
 * Create a session and attach `files` to it in a follow-up call. If the
 * attach fails, a best-effort rollback deletes the just-created session and
 * the attach error — not the rollback error — is surfaced; when the rollback
 * delete itself fails, the failure is reported to monitoring and the orphan
 * session remains on the server. On success the returned record seeds the
 * session's detail cache and the session lists are invalidated.
 *
 * The rollback must span both service calls, which is why this is a single
 * mutation instead of a composition of `useCreateSession` and
 * `useAddFilesToSession`.
 */
export function useCreateSessionFromFiles() {
  const { sessionService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<SessionRecord, Error, UseCreateSessionFromFilesVariables>({
    mutationFn: async ({ request, files, resourceOwnerId }) => {
      // The create route authorizes on the caller's org (REQUIRES_CALLER_ORG)
      // and takes the org header; the file-attach and delete routes are
      // owner-scoped and take the resource-owner header. Both headers carry
      // the caller's current org id.
      const session = await sessionService.createSession(request, {
        orgId: resourceOwnerId,
      });

      if (files.length === 0) {
        return session;
      }

      try {
        return await sessionService.addFilesToSession(
          session.session_id,
          files,
          { resourceOwnerId },
        );
      } catch (error) {
        // Attach failed: delete the session, then re-throw the attach
        // error. The session was created by this same mutation, so the
        // delete destroys nothing the user had before.
        try {
          await sessionService.deleteSession(session.session_id, {
            resourceOwnerId,
          });
        } catch (rollbackError) {
          // Swallow a rollback failure (report it to monitoring); the
          // original attach error below still propagates.
          ErrorMonitoringService.captureError(rollbackError);
        }
        throw error;
      }
    },
    onSuccess: (record) => {
      queryClient.setQueryData(
        sessionQueryKeys.detail(record.session_id),
        record,
      );
      void queryClient.invalidateQueries({
        queryKey: sessionQueryKeys.lists(),
      });
    },
  });
}
